// Platform tagging — vendor rollups over Salesforce's `Project_Platform__c`.
//
// NOT to be confused with the unrelated `platform_constants.ts` in this same
// directory, which holds a Hub logout-URL sentinel (CQ-07).
//
// `Project_Platform__c` is a governed multi-picklist (130 active values,
// ';'-delimited when an opportunity carries several). Users think in vendor
// families ("how much Adobe work do we have?"), so Deal Explorer offers a
// family filter alongside the raw-value filter.
//
// Matching is two-tier:
//   1. A whole-word vendor prefix — most Adobe members are literally prefixed
//      "Adobe", most Salesforce members "Salesforce". This self-extends as
//      Sales Ops adds picklist values, and still classifies RETIRED values that
//      survive only on historical opportunities.
//   2. An explicit alias list for acquired products that kept their original
//      brand ("Workfront" is Adobe, "Tableau" is Salesforce). This is the one
//      part that needs maintaining when the picklist changes.
//
// Aliases match on a whole-word prefix too, not whole-value equality, so a
// variant like "Workfront Fusion" rolls up correctly (CQ-01).
//
// The ';'-delimited + drop-blank + drop-"NONE" rule is shared with two backend
// call sites — `queries.split_multipicklist` and `classifier._split_enablement`
// (ARCH-04 / CQ-02). Full reuse isn't possible across the language boundary,
// and the classifier lower-cases its output where we must preserve casing for
// display; if SF ever adds a second "no value" sentinel, all three need it.

/** Vendor families, in dropdown order. `Other` is the catch-all and stays last. */
export const PLATFORM_FAMILIES = [
  "Adobe",
  "Salesforce",
  "Google",
  "Microsoft",
  "Acquia",
  "Oracle",
  "Other",
] as const;

export type PlatformFamily = (typeof PLATFORM_FAMILIES)[number];

/** The catch-all; every recognised-but-unfamilied platform lands here. */
const OTHER: PlatformFamily = "Other";

/** Narrow an untrusted string (URL param, DOM value) to a known family. */
export function isPlatformFamily(value: string): value is PlatformFamily {
  return (PLATFORM_FAMILIES as readonly string[]).includes(value);
}

/**
 * Acquired products that kept their original brand and so carry no vendor
 * prefix. Verified against BOTH the 130 active picklist values and 9,053
 * live tagged opportunities (133 distinct members) on 2026-09-21 — the live
 * sweep is what matters, because retired values like "Magento" and
 * "Exact target" no longer appear in the active picklist but are still
 * present on historical opportunities (ARCH-02).
 *
 * Keys are lower-cased and matched as a whole-word PREFIX.
 */
const FAMILY_ALIASES: ReadonlyArray<readonly [string, PlatformFamily]> = [
  // Adobe acquisitions. Note "Marketo" and "Adobe Marketo" both exist as
  // separate picklist values; "Magento" is the retired name for Adobe Commerce.
  ["workfront", "Adobe"],
  ["marketo", "Adobe"],
  ["magento", "Adobe"],
  // Salesforce acquisitions. "Exact target" predates Marketing Cloud;
  // Tableau has been Salesforce-owned since 2019.
  ["pardot", "Salesforce"],
  ["tableau", "Salesforce"],
  ["exact target", "Salesforce"],
  // Oracle's marketing-cloud acquisitions.
  ["eloqua", "Oracle"],
  ["responsys", "Oracle"],
  ["blue kai", "Oracle"],
  ["maxymizer", "Oracle"],
];

/** Picklist values that mean "no platform" and must never look like one. */
const NOT_A_PLATFORM: ReadonlySet<string> = new Set(["none"]);

/**
 * Whole-word prefix test: `token` must start `member` and be followed by a
 * non-word character or end-of-string. Keeps "Marketoonist" out of "marketo"
 * while letting "Workfront Fusion" match "workfront".
 */
function startsWithToken(memberLower: string, token: string): boolean {
  if (!memberLower.startsWith(token)) return false;
  const next = memberLower.charAt(token.length);
  return next === "" || !/[a-z0-9]/.test(next);
}

/** Family for a single platform member. Recognised-but-unfamilied → `Other`. */
function familyOfMember(member: string): PlatformFamily {
  const lower = member.toLowerCase();
  for (const [token, family] of FAMILY_ALIASES) {
    if (startsWithToken(lower, token)) return family;
  }
  for (const family of PLATFORM_FAMILIES) {
    if (family !== OTHER && startsWithToken(lower, family.toLowerCase())) return family;
  }
  return OTHER;
}

/**
 * Split the ';'-delimited multi-picklist into individual platform members.
 * Blanks and the `NONE` sentinel are dropped, so an opportunity explicitly
 * marked NONE reads the same as one that was never tagged.
 */
export function splitPlatforms(raw: string | null | undefined): string[] {
  if (!raw) return [];
  return raw
    .split(";")
    .map((p) => p.trim())
    .filter((p) => p !== "" && !NOT_A_PLATFORM.has(p.toLowerCase()));
}

/**
 * Distinct families an opportunity's platform value rolls up to. A
 * multi-platform opportunity belongs to several families at once, so a family
 * filter is a "has any" test, not an equality test.
 */
export function familiesOf(raw: string | null | undefined): PlatformFamily[] {
  const seen = new Set<PlatformFamily>();
  for (const member of splitPlatforms(raw)) seen.add(familyOfMember(member));
  return [...seen];
}
